import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authApi } from '../utils/api';

interface Account {
  id: string;
  name: string;
  type: 'individual' | 'business';
  settings?: {
    marketingOptIn: boolean;
    notifications: {
      email: boolean;
      sms: boolean;
    };
    timezone: string;
    currency: string;
  };
}

interface User {
  id: string;
  account: Account;
  email: string;
  name?: string;
  profilePicture?: string;
  role: 'buyer' | 'seller' | 'admin';
  accountRole: 'owner' | 'admin' | 'user';
  permissions: string[];
  lastLoginAt?: string;
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  requestOtp: (email: string) => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  console.log('[AuthProvider] Component rendering');
  
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on mount - ONLY ONCE
  useEffect(() => {
    console.log('[AuthProvider] useEffect running - checking auth');
    
    const token = localStorage.getItem('token');
    if (token) {
      console.log('[AuthProvider] Token found, calling /api/auth/me');
      // Call API once to check auth status
      authApi.getMe()
        .then(response => {
          if (response.data.success && response.data.user) {
            console.log('[AuthProvider] User authenticated:', response.data.user.email);
            setUser(response.data.user);
          } else {
            console.log('[AuthProvider] Auth check failed - no user in response');
            setUser(null);
          }
        })
        .catch((err) => {
          console.log('[AuthProvider] Auth check failed with error:', err.message);
          setUser(null);
          localStorage.removeItem('token');
        })
        .finally(() => {
          console.log('[AuthProvider] Auth check complete');
          setLoading(false);
        });
    } else {
      console.log('[AuthProvider] No token found');
      setLoading(false);
    }
  }, []); // Empty array - only run ONCE on mount

  const requestOtp = async (email: string): Promise<void> => {
    try {
      await authApi.requestOtp(email);
    } catch (error: any) {
      const message = error.response?.data?.error?.message || 'Failed to send OTP';
      throw new Error(message);
    }
  };

  const login = async (email: string, code: string): Promise<void> => {
    try {
      const response = await authApi.verifyOtp(email, code);
      if (response.data.success && response.data.user) {
        setUser(response.data.user);
        // Store token in localStorage as backup
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
      } else {
        throw new Error('Login failed');
      }
    } catch (error: any) {
      const message = error.response?.data?.error?.message || 'Login failed';
      throw new Error(message);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('token');
      // Force redirect to home page after logout
      window.location.href = '/';
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    requestOtp,
    updateUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
